import { CameraProjection } from '@skand/three-js-renderer';
import { Cartographic } from '@skand/three-js-renderer/bin/private/math';
import { Quaternion, Vector3 } from 'three';
import { calculateCameraTargetPosition, calculateNadirOrientation, toCartesian, toRadians, } from '../../Utils';
import { Service } from '../Service';
import { CameraOrbit, CameraPanorama, CameraSuperman, } from './CameraModes';
/**
 * Default camera position.
 */
const DEFAULT_CAMERA_POSITION = toCartesian(new Cartographic(toRadians(150.898042), toRadians(-34.464009), 2500000));
/**
 * Default camera rotation.
 */
const DEFAULT_CAMERA_ROTATION = calculateNadirOrientation(DEFAULT_CAMERA_POSITION);
/**
 * API for manipulating the camera and its controls.
 */
export class NavigationService extends Service {
    constructor(modules) {
        super(modules);
        Object.defineProperty(this, "camera", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "underground", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraMotionCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraStopCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.camera = this.renderer.getWorld().getCamera();
        this.underground = true;
        this.cameraMotionCallback = () => { };
        this.cameraStopCallback = () => { };
        this.currentMode = new CameraOrbit(this.renderer, this.gestures);
        this.currentMode.start();
        this.moveTo(DEFAULT_CAMERA_POSITION, DEFAULT_CAMERA_ROTATION);
    }
    /**
     * Start a camera mode.
     */
    start(cameraMode) {
        this.currentMode.stop();
        this.currentMode = cameraMode;
        this.currentMode.start();
        this.currentMode.moveTo(this.getPosition(), this.getRotation());
        this.currentMode.setUnderground(this.underground);
        this.currentMode.setCameraMotionCallback(this.cameraMotionCallback);
        this.currentMode.setCameraStopCallback(this.cameraStopCallback);
    }
    /**
     * Enable camera controls.
     */
    enable() {
        this.currentMode.start();
    }
    /**
     * Disable camera controls.
     */
    disable() {
        this.currentMode.stop();
    }
    /**
     * Set the current navigation mode.
     *
     * @param mode
     */
    setNavigationMode(mode) {
        switch (mode) {
            case 'orbit':
                this.start(new CameraOrbit(this.renderer, this.gestures));
                break;
            case 'superman':
                this.start(new CameraSuperman(this.renderer, this.gestures));
                break;
            case 'panorama':
                this.start(new CameraPanorama(this.renderer, this.gestures));
                break;
        }
    }
    /**
     * Get the current navigation mode.
     *
     * @returns
     */
    getNavigationMode() {
        switch (this.currentMode.constructor) {
            case CameraOrbit:
                return 'orbit';
            case CameraSuperman:
                return 'superman';
            case CameraPanorama:
                return 'panorama';
            default:
                throw new Error('Unknown camera mode');
        }
    }
    /**
     * Set the projection mode of the camera.
     *
     * @param projectionMode
     */
    setProjectionMode(mode) {
        switch (mode) {
            case 'perspective':
                this.camera.setProjectionMode(CameraProjection.Perspective);
                break;
            case 'orthographic':
                this.camera.setProjectionMode(CameraProjection.Orthographic);
                break;
        }
    }
    /**
     * Get the projection mode of the camera.
     *
     * @returns
     */
    getProjectionMode() {
        switch (this.camera.getProjectionMode()) {
            case CameraProjection.Perspective:
                return 'perspective';
            case CameraProjection.Orthographic:
                return 'orthographic';
        }
    }
    /**
     * Reset the camera to the default position and orientation.
     */
    resetCamera() {
        this.flyTo(DEFAULT_CAMERA_POSITION, DEFAULT_CAMERA_ROTATION);
    }
    /**
     * Toggle allowing the camera to go underground (globe surface).
     *
     * @param underground
     */
    setUnderground(underground) {
        this.underground = underground;
        this.currentMode.setUnderground(underground);
    }
    /**
     * Set the camera motion callback.
     *
     * @param underground
     */
    setCameraMotionCallback(callback) {
        this.cameraMotionCallback = callback;
        this.currentMode.setCameraMotionCallback(callback);
    }
    /**
     * Set the camera stop callback.
     *
     * @param callback
     */
    setCameraStopCallback(callback) {
        this.cameraStopCallback = callback;
        this.currentMode.setCameraStopCallback(callback);
    }
    /**
     * Smoothly fly the camera to a position and orientation configuration.
     *
     * @param position
     * @param rotation
     */
    flyTo(position, rotation) {
        if (!rotation) {
            rotation = calculateNadirOrientation(position);
        }
        this.currentMode.flyTo(position, rotation);
    }
    /**
     * Teleport the camera to a position and orientation configuration.
     *
     * @param position
     * @param rotation
     */
    moveTo(position, rotation) {
        if (!rotation) {
            rotation = calculateNadirOrientation(position);
        }
        this.currentMode.moveTo(position, rotation);
    }
    /**
     * Get the position of the camera in ECEF.
     *
     * @param position
     * @returns
     */
    getPosition(position) {
        if (!position)
            position = new Vector3();
        this.camera.getPositionECEF(position);
        return position;
    }
    /**
     * Get the rotation of the camera.
     *
     * @param rotation
     * @returns
     */
    getRotation(rotation) {
        if (!rotation)
            rotation = new Quaternion();
        this.camera.getRotation(rotation);
        return rotation;
    }
    /**
     * Look at a model.
     *
     * @param model
     * @param flyTo
     */
    lookAt(model, flyTo = true) {
        const center = model.getPosition();
        const size = model.getBoundingRadius();
        const fov = this.camera.getPerspective().getFieldOfView();
        const targetPosition = calculateCameraTargetPosition(center, size, toRadians(fov));
        const targetRotation = calculateNadirOrientation(targetPosition);
        if (flyTo) {
            this.flyTo(targetPosition, targetRotation);
        }
        else {
            this.moveTo(targetPosition, targetRotation);
        }
    }
    /**
     * Per-frame updates.
     *
     * @param deltaTime
     */
    updateFrame(deltaTime) {
        this.currentMode.updateFrame(deltaTime);
    }
}
